import { TREND_SECTIONS, type Me, type TrendCard, type TrendSection } from '@trendator/shared';
import { useEffect, useState } from 'react';
import { api } from '../api';
import { BackIcon, SearchIcon } from '../components/icons';
import { rub } from '../format';
import { useBackButton } from '../hooks';
import { haptic } from '../telegram';
import { Loading } from './Status';

/** Цвета плиток разделов — из docs/design/Catalog.dc.html. */
const TILE: Record<TrendSection, string> = {
  self: 'linear-gradient(150deg, rgba(185,166,255,0.35), rgba(106,79,216,0.15))',
  gifts: 'linear-gradient(150deg, rgba(255,154,98,0.35), rgba(194,65,107,0.15))',
  business: 'linear-gradient(150deg, rgba(111,211,255,0.35), rgba(59,107,217,0.15))',
  kids: 'linear-gradient(150deg, rgba(124,242,200,0.35), rgba(46,156,143,0.15))',
  tryon: 'linear-gradient(150deg, rgba(255,214,107,0.35), rgba(224,123,57,0.15))',
  songs: 'linear-gradient(150deg, rgba(255,107,170,0.35), rgba(120,40,140,0.15))',
};

const sectionParts = (label: string) => {
  const [emoji, ...rest] = label.split(' ');
  return { emoji: emoji!, name: rest.join(' ') };
};

/** Цена на карточке: бесплатно, если тренд это разрешает и попытка не потрачена. */
export function trendPrice(t: Pick<TrendCard, 'price' | 'allowFree'>, me: Me): string {
  return t.allowFree && me.freeGenerationAvailable ? 'Бесплатно' : rub(t.price);
}

export function TrendThumb({ t, className }: { t: Pick<TrendCard, 'coverUrl' | 'coverGradient'>; className?: string }) {
  return (
    <span
      className={className}
      style={t.coverUrl ? { backgroundImage: `url("${t.coverUrl}")`, backgroundSize: 'cover', backgroundPosition: 'center' } : { background: t.coverGradient }}
    />
  );
}

/** Все тренды — docs/design/Catalog.dc.html. */
export function Catalog({
  me,
  initialSection,
  onBack,
  onOpen,
}: {
  me: Me;
  initialSection?: TrendSection;
  onBack: () => void;
  onOpen: (t: TrendCard) => void;
}) {
  useBackButton(onBack);
  const [items, setItems] = useState<TrendCard[] | null>(null);
  const [error, setError] = useState(false);
  const [q, setQ] = useState('');
  const [section, setSection] = useState<TrendSection | null>(initialSection ?? null);

  useEffect(() => {
    api
      .trends()
      .then(setItems)
      .catch(() => setError(true));
  }, []);

  const query = q.trim().toLowerCase();
  const shown = (items ?? []).filter(
    (t) =>
      (!section || t.section === section) &&
      (!query || t.title.toLowerCase().includes(query) || t.subtitle.toLowerCase().includes(query)),
  );
  const groups = TREND_SECTIONS.map((s) => ({ ...s, items: shown.filter((t) => t.section === s.id) })).filter((g) => g.items.length);

  return (
    <main className="screen catalog">
      <header className="catalog__header">
        <button type="button" className="glass icon-btn" aria-label="Назад" onClick={onBack}>
          <BackIcon />
        </button>
        <h1 className="catalog__title">Все тренды</h1>
        {items ? <span className="catalog__count">{items.length} шт.</span> : null}
      </header>

      <label className="search">
        <SearchIcon />
        <span className="sr-only">Поиск трендов</span>
        <input type="search" placeholder="Паспорт, аниме, Авито…" value={q} onChange={(e) => setQ(e.target.value)} />
      </label>

      <div className="sections" role="group" aria-label="Разделы">
        {TREND_SECTIONS.map((s) => {
          const { emoji, name } = sectionParts(s.label);
          return (
            <button
              key={s.id}
              type="button"
              className="section-tile"
              aria-pressed={section === s.id}
              style={{ background: TILE[s.id] }}
              onClick={() => {
                haptic('select');
                setSection(section === s.id ? null : s.id);
              }}
            >
              <span className="section-tile__emoji">{emoji}</span>
              <span className="section-tile__name">{name}</span>
            </button>
          );
        })}
      </div>

      {error ? <p className="error-text catalog__msg">Не получилось загрузить тренды. Проверь интернет.</p> : null}
      {!items && !error ? <Loading inline /> : null}
      {items && groups.length === 0 ? (
        <p className="catalog__msg">{items.length ? 'Ничего не нашли — попробуй другое слово' : 'Тренды скоро появятся. Пока можно сделать фото в ручном режиме.'}</p>
      ) : null}

      {groups.map((g) => (
        <section key={g.id}>
          <h2 className="catalog__h2">{sectionParts(g.label).name}</h2>
          <div className="glass-list catalog__list">
            {g.items.map((t, i) => (
              <div key={t.id}>
                {i > 0 ? <div className="catalog__sep" /> : null}
                <button
                  type="button"
                  className="catalog__row"
                  onClick={() => {
                    haptic();
                    onOpen(t);
                  }}
                >
                  <TrendThumb t={t} className="catalog__thumb" />
                  <span className="catalog__text">
                    <span className="catalog__name">{t.title}</span>
                    {t.subtitle ? <span className="catalog__sub">{t.subtitle}</span> : null}
                  </span>
                  <span className="catalog__price">{trendPrice(t, me)}</span>
                </button>
              </div>
            ))}
          </div>
        </section>
      ))}
    </main>
  );
}
